import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { ReportRoomResponseV4Schema } from "../response/ReportRoomResponseV4";

const API_VERSION = 'v4';

// Tipe spesifik untuk kolom yang bisa diurutkan
type OrderByOption = "room_type" | "percentage" | "room_nights" | "reservations" | "avg_nights" | "booking_window" | "revenue" | "ota_com" | "avg_rate" | "cancel_count";

// Antarmuka untuk parameter filter di body request
interface FilterPropsV4 {
    property_id: string;
    start_date: string;
    end_date: string;
    base_on: "stay_date" | "created_reservation";
    room_type_id?: string;
}

// Antarmuka untuk parameter hook
interface ReportRoomV4Props {
    filter: FilterPropsV4;
    orderBy?: OrderByOption; // Menggunakan tipe yang sudah didefinisikan
    sort?: "asc" | "desc";
    type: string | null;
}

/**
 * Hook React Query untuk mengambil laporan kamar (room report) v4.
 *
 * @param params - Filter, opsi pengurutan, dan tipe laporan.
 * @returns Hasil dari hook useQuery.
 */
const useReportRoomV4 = (params: ReportRoomV4Props) => {
    return useQuery({
        queryKey: [
            'report',
            'report-room-v4',
            params.type,
            JSON.stringify(params.filter),
            params.orderBy,
            params.sort,
        ],
        queryFn: async () => {
            try {
                // Menyusun body request
                const requestBody = {
                    filter: params.filter,
                    orderBy: params.orderBy || "room_type", // orderBy default
                    sort: params.sort || "desc", // sort default
                };

                const response = await privateApi.get(`/${API_VERSION}/channel-manager/property-report-room`, { params: requestBody });

                // Validasi respons dengan skema Zod
                const validationResult = ReportRoomResponseV4Schema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid V4 Room report data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch V4 Room report`, error);
                throw error;
            }
        },
        enabled: !!params.filter && !!params.filter.property_id && !!params.filter.start_date && !!params.filter.end_date && params.type === "room_report",
        retry: 1,
        refetchOnWindowFocus: false,
    });
};

export default useReportRoomV4;